import { WtwBreakdownTimeseries } from '@lune-fe/dapi-models'
import { EmissionsChartSection, wtwBreakdownChartColors } from '@lune-fe/lune-components-lib'
import Box from '@mui/material/Box'
import moment from 'moment'
import { Range } from 'react-date-range'

import WhiteBoxContainerWithContent from 'components/WhiteBoxContainerWithContent'

/**
 * Calculates carbon intensity breakdown for WTT and TTW stages by combining
 * average carbon intensity with proportional emission breakdowns.
 */
const calculateCarbonIntensityBreakdown = (
    intensityData?: Array<{ date: string; average: number }>,
    wtwData?: WtwBreakdownTimeseries,
): { date: string; wtt: number; ttw: number }[] => {
    if (!intensityData || !wtwData || intensityData.length === 0 || wtwData.length === 0) {
        return []
    }

    const intensityMap = new Map(intensityData.map((item) => [item.date, item.average]))

    return wtwData
        .map((wtwItem) => {
            const averageIntensity = intensityMap.get(wtwItem.date)
            if (!averageIntensity) {
                return null
            }
            const totalEmissions = wtwItem.wtt + wtwItem.ttw
            if (totalEmissions === 0) {
                return {
                    date: wtwItem.date,
                    wtt: 0,
                    ttw: 0,
                }
            }
            // Calculate carbon intensity for each stage proportionally (excluding unknown)
            const wttProportion = wtwItem.wtt / totalEmissions
            const ttwProportion = wtwItem.ttw / totalEmissions
            return {
                date: wtwItem.date,
                wtt: averageIntensity * wttProportion,
                ttw: averageIntensity * ttwProportion,
            }
        })
        .filter((item): item is { date: string; wtt: number; ttw: number } => item !== null)
}

const CarbonIntensityTrend = ({
    loading,
    wtwBreakdownTimeseries,
    carbonIntensityTimeseries,
    dateRange,
}: {
    loading: boolean
    wtwBreakdownTimeseries?: WtwBreakdownTimeseries
    carbonIntensityTimeseries?: Array<{
        date: string
        average: number
    }>
    dateRange: Range
}) => {
    const carbonIntensityBreakdown = calculateCarbonIntensityBreakdown(
        carbonIntensityTimeseries,
        wtwBreakdownTimeseries,
    )

    // Create a function to get tooltip header items (WTW average from carbon intensity timeseries)
    const getCarbonIntensityTooltipHeaderItems = (() => {
        const intensityData = carbonIntensityTimeseries
        if (!intensityData || intensityData.length === 0) {
            return undefined
        }

        // Create a map from formatted dates to carbon intensity values
        const rangeDuration = moment.duration(moment(dateRange.endDate).diff(dateRange.startDate))
        const dateFormat =
            !dateRange.endDate || !dateRange.startDate || rangeDuration.asMonths() > 1
                ? 'MMM, YYYY'
                : 'DD MMM, YYYY'

        const dateToIntensityMap = new Map<string, number>()
        intensityData.forEach((item) => {
            const formattedDate = moment(item.date).format(dateFormat)
            dateToIntensityMap.set(formattedDate, item.average)
        })

        return (tooltipDate: string): Array<{ label: string; value: number }> => {
            const intensity = dateToIntensityMap.get(tooltipDate)
            if (intensity !== undefined) {
                return [{ label: 'WTW', value: intensity }]
            }
            return []
        }
    })()

    return (
        <WhiteBoxContainerWithContent
            title={'Carbon intensity trend'}
            sx={{
                p: 1,
                width: '100%',
                flex: '1 1 auto',
            }}
        >
            <Box sx={{ width: '100%' }}>
                <EmissionsChartSection<'wtt' | 'ttw'>
                    chartHeight={315}
                    loading={loading}
                    data={carbonIntensityBreakdown}
                    dataColours={{
                        wtt: wtwBreakdownChartColors.wtt,
                        ttw: wtwBreakdownChartColors.ttw,
                    }}
                    dataLabels={{
                        wtt: 'Well-To-Tank',
                        ttw: 'Tank-To-Wheel',
                    }}
                    dateRange={dateRange}
                    showLegend={true}
                    skipZeroOnTooltip={true}
                    yAxisLabel="gCO₂e/t*km"
                    getTooltipHeaderItems={getCarbonIntensityTooltipHeaderItems}
                    labelOrdering={(a, b) => {
                        if (a === 'wtt') return -1
                        if (b === 'wtt') return 1
                        return 0
                    }}
                />
            </Box>
        </WhiteBoxContainerWithContent>
    )
}

export default CarbonIntensityTrend
